<script setup lang="ts">
/**
 * Treasury accounts CRUD container.
 *
 * Composes:
 *   - list query (returns active accounts by default; toggle exposes
 *     inactive too — for now we show all by passing no `status`)
 *   - create / update / deactivate / set-default mutations
 *   - form drawer (shared between create + edit modes)
 *   - "Set as default" + "Deactivate" confirm dialogs
 *
 * Idempotency keys generate on dialog/drawer open and reset on close.
 * Mutations invalidate the list query on success.
 */
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Message from '@uiKit/elements/Message.vue'
import DeleteConfirmDialog from '@uiKit/components/DeleteConfirmDialog.vue'

import { formatToInternational, getIdempotencyKey } from '@shared/utils'
import { useAppToast } from '@shared/composables/useAppToast'
import { routes } from '@app/router/routes'

import TreasuryAccountsList from '../components/TreasuryAccountsList.vue'
import TreasuryAccountFormDrawer from '../components/TreasuryAccountFormDrawer.vue'
import type { TreasuryAccountFormValues } from '../components/TreasuryAccountForm.vue'

import { useTreasuryBankAccountsQuery } from '../api/queries/useTreasuryBankAccountsQuery'
import { useCreateTreasuryAccountMutation } from '../api/mutations/useCreateTreasuryAccountMutation'
import { useUpdateTreasuryAccountMutation } from '../api/mutations/useUpdateTreasuryAccountMutation'
import { useDeactivateTreasuryAccountMutation } from '../api/mutations/useDeactivateTreasuryAccountMutation'
import { useSetDefaultTreasuryAccountMutation } from '../api/mutations/useSetDefaultTreasuryAccountMutation'

import type { TreasuryBankAccount } from '../types/treasuryAccountsTypes'

// ─── List query ─────────────────────────────────────────────────────────────

// Pass no `status` filter — the picker hooks default to ACTIVE only;
// the CRUD list needs both active + inactive.
const {
  data: accounts,
  isLoading,
  error: listError,
  refetch,
} = useTreasuryBankAccountsQuery(() => ({}))

const totalAccounts = computed(() => accounts.value?.length ?? 0)
const headerCountLabel = computed(() => {
  if (isLoading.value) return '…'
  return `${formatToInternational(totalAccounts.value)} accounts`
})

// Local pagination state — feeds DataTableV2's url-sync + storage-key
// integration so refresh / back-nav and operator preference both stick.
const currentPage = ref(1)
const pageSize = ref(50)

// ─── Form drawer (create + edit) ────────────────────────────────────────────

const formDrawerVisible = ref(false)
const formMode = ref<'create' | 'edit'>('create')
const editingAccount = ref<TreasuryBankAccount | null>(null)
const formIdempotencyKey = ref('')

const { addToast } = useAppToast()

const {
  mutate: createMutate,
  isPending: isCreating,
  error: createError,
  reset: resetCreate,
} = useCreateTreasuryAccountMutation()
const {
  mutate: updateMutate,
  isPending: isUpdating,
  error: updateError,
  reset: resetUpdate,
} = useUpdateTreasuryAccountMutation()

function describeError(err: unknown): string {
  if (!err) return ''
  if (err instanceof Error) return err.message
  const candidate = err as { message?: unknown; code?: unknown }
  if (typeof candidate.message === 'string') return candidate.message
  if (typeof candidate.code === 'string') return candidate.code
  return 'Something went wrong. Please try again.'
}

const formError = computed(() => {
  if (formMode.value === 'create') return describeError(createError.value)
  return describeError(updateError.value)
})

const isFormSubmitting = computed(() =>
  formMode.value === 'create' ? isCreating.value : isUpdating.value,
)

function openCreate() {
  formMode.value = 'create'
  editingAccount.value = null
  formIdempotencyKey.value = getIdempotencyKey()
  resetCreate()
  resetUpdate()
  formDrawerVisible.value = true
}

function openEdit(row: TreasuryBankAccount) {
  formMode.value = 'edit'
  editingAccount.value = row
  formIdempotencyKey.value = getIdempotencyKey()
  resetCreate()
  resetUpdate()
  formDrawerVisible.value = true
}

function closeForm() {
  formDrawerVisible.value = false
  editingAccount.value = null
  formIdempotencyKey.value = ''
}

const formInitialValues = computed(() => {
  if (!editingAccount.value) return {}
  return {
    accountName: editingAccount.value.accountName,
    bankName: editingAccount.value.bankName,
    currencyCode: editingAccount.value.currencyCode,
    countryCode: editingAccount.value.countryCode,
  }
})

function handleFormSubmit(values: TreasuryAccountFormValues) {
  if (formMode.value === 'create') {
    createMutate(
      {
        accountName: values.accountName,
        bankName: values.bankName,
        accountNumber: values.accountNumber,
        iban: values.iban || null,
        swiftBic: values.swiftBic || null,
        currencyCode: values.currencyCode,
        countryCode: values.countryCode,
        notes: values.notes || null,
        isDefault: values.isDefault,
        idempotencyKey: formIdempotencyKey.value,
      },
      {
        onSuccess: () => {
          addToast({
            type: 'success',
            title: 'Treasury account created',
            message: `${values.accountName} is now available for settlement.`,
          })
          closeForm()
        },
      },
    )
    return
  }
  if (!editingAccount.value) return
  updateMutate(
    {
      id: editingAccount.value.id,
      accountName: values.accountName,
      bankName: values.bankName,
      iban: values.iban || null,
      swiftBic: values.swiftBic || null,
      notes: values.notes || null,
    },
    {
      onSuccess: () => {
        addToast({
          type: 'success',
          title: 'Treasury account updated',
          message: `${values.accountName} saved.`,
        })
        closeForm()
      },
    },
  )
}

// ─── View transactions deep link ────────────────────────────────────────────
//
// Navigate to the fiat-transactions list pre-filtered to this treasury
// account's transactions. The list container reads
// `route.query.treasuryAccountId` on mount.
const router = useRouter()

function handleViewTransactions(row: TreasuryBankAccount) {
  router.push({
    path: routes.TREASURY_FIAT_TRANSACTIONS_LIST,
    query: { treasuryAccountId: row.id },
  })
}

// ─── Set-default + Deactivate confirms ──────────────────────────────────────

const setDefaultDialogVisible = ref(false)
const deactivateDialogVisible = ref(false)
const targetRow = ref<TreasuryBankAccount | null>(null)
const actionIdempotencyKey = ref('')

const {
  mutate: setDefaultMutate,
  isPending: isSettingDefault,
  reset: resetSetDefault,
} = useSetDefaultTreasuryAccountMutation()
const {
  mutate: deactivateMutate,
  isPending: isDeactivating,
  reset: resetDeactivate,
} = useDeactivateTreasuryAccountMutation()

function openSetDefault(row: TreasuryBankAccount) {
  targetRow.value = row
  actionIdempotencyKey.value = getIdempotencyKey()
  resetSetDefault()
  setDefaultDialogVisible.value = true
}

function openDeactivate(row: TreasuryBankAccount) {
  targetRow.value = row
  actionIdempotencyKey.value = getIdempotencyKey()
  resetDeactivate()
  deactivateDialogVisible.value = true
}

function handleSetDefaultConfirm() {
  if (!targetRow.value) return
  setDefaultMutate(
    {
      id: targetRow.value.id,
      idempotencyKey: actionIdempotencyKey.value,
    },
    {
      onSuccess: () => {
        const name = targetRow.value?.accountName ?? 'Treasury account'
        const currency = targetRow.value?.currencyCode ?? ''
        addToast({
          type: 'success',
          title: 'Default updated',
          message: `${name} is now the default for ${currency}.`,
        })
        setDefaultDialogVisible.value = false
        targetRow.value = null
      },
      onError: (err) => {
        addToast({
          type: 'error',
          title: 'Could not set as default',
          message: describeError(err),
        })
      },
    },
  )
}

function handleDeactivateConfirm() {
  if (!targetRow.value) return
  deactivateMutate(
    {
      id: targetRow.value.id,
      idempotencyKey: actionIdempotencyKey.value,
    },
    {
      onSuccess: () => {
        const name = targetRow.value?.accountName ?? 'Treasury account'
        addToast({
          type: 'success',
          title: 'Treasury account deactivated',
          message: `${name} will no longer appear in approval pickers.`,
        })
        deactivateDialogVisible.value = false
        targetRow.value = null
      },
      onError: (err) => {
        addToast({
          type: 'error',
          title: 'Could not deactivate',
          message: describeError(err),
        })
      },
    },
  )
}

const setDefaultTitle = computed(
  () => `Make ${targetRow.value?.accountName ?? 'this account'} the default?`,
)
const setDefaultMessage = computed(() => {
  if (!targetRow.value) return ''
  return `Other accounts for ${targetRow.value.currencyCode} will lose the default flag.`
})

const deactivateTitle = computed(
  () => `Deactivate ${targetRow.value?.accountName ?? 'this account'}?`,
)
const deactivateMessage = computed(() => 'It will no longer appear in approval pickers.')
</script>

<template>
  <Box flex direction="col" gap="4">
    <Box flex justify="between" align="center" gap="3">
      <Box flex direction="col" gap="1">
        <Text type="page-title">Treasury accounts</Text>
        <Text type="body-sm" color="muted">{{ headerCountLabel }}</Text>
      </Box>
      <Button label="Add account" color="primary" icon="plus" @click="openCreate" />
    </Box>

    <Message v-if="listError" color="danger" variant="outlined" size="small">
      <Box flex justify="between" align="center" gap="3">
        <Text type="body-sm">Could not load treasury accounts. {{ describeError(listError) }}</Text>
        <Button label="Try again" variant="text" color="danger" @click="refetch()" />
      </Box>
    </Message>

    <TreasuryAccountsList
      :rows="accounts ?? []"
      :loading="isLoading"
      :total="totalAccounts"
      :page="currentPage"
      :page-size="pageSize"
      @update:page="currentPage = $event"
      @update:page-size="pageSize = $event"
      @view-transactions="handleViewTransactions"
      @edit="openEdit"
      @set-default="openSetDefault"
      @deactivate="openDeactivate"
    />
  </Box>

  <TreasuryAccountFormDrawer
    :visible="formDrawerVisible"
    :mode="formMode"
    :initial-values="formInitialValues"
    :loading="isFormSubmitting"
    :api-error="formError"
    @update:visible="formDrawerVisible = $event"
    @submit="handleFormSubmit"
  />

  <DeleteConfirmDialog
    :visible="setDefaultDialogVisible"
    severity="info"
    :title="setDefaultTitle"
    :message="setDefaultMessage"
    confirm-label="Set as default"
    :loading="isSettingDefault"
    @update:visible="setDefaultDialogVisible = $event"
    @confirm="handleSetDefaultConfirm"
    @cancel="setDefaultDialogVisible = false"
  />

  <DeleteConfirmDialog
    :visible="deactivateDialogVisible"
    severity="danger"
    :title="deactivateTitle"
    :message="deactivateMessage"
    confirm-label="Deactivate"
    :loading="isDeactivating"
    @update:visible="deactivateDialogVisible = $event"
    @confirm="handleDeactivateConfirm"
    @cancel="deactivateDialogVisible = false"
  />
</template>
